import React, { useCallback, useState } from "react";
import styled from "styled-components";
import config from "../../config";
import ModalBasic from "./ModalBasic.jsx";
import thumbsUpImg from "../../img/user-thumbs-up.png";
import thumbsDownImg from "../../img/user-thumbs-down.png";
import exclamationMark from "../../img/exclamation_mark.png";
import closeButton from "../../img/close_button.png";
import { apiFetch } from "../../utils/apiClient.js";

const ModalUserFeedback = ({ isOpen, onClose, onSubmit, onShowWarning }) => {
  const [selected, setSelected] = useState("NONE"); // "LIKE", "DISLIKE", "NONE"
  const [opinion, setOpinion] = useState(""); // 사용자 의견 (선택사항)
  const [toastMessage, setToastMessage] = useState(""); // toast 메시지
  const [showToast, setShowToast] = useState(false); // toast 표시 여부
  const opinionGuide =
    selected === "LIKE"
      ? "문제 해결에 어떻게 도움이 됐나요?"
      : selected === "DISLIKE"
        ? "어떤 부분이 어떻게 개선되면 좋을까요?"
        : "필수 기재 항목입니다.";

  // toast 표시 함수
  const showToastMessage = (message) => {
    setToastMessage(message);
    setShowToast(true);
    setTimeout(() => {
      setShowToast(false);
      setToastMessage("");
    }, 3000); // 3초 후 사라짐
  };

  //  textarea 변경 핸들러 - 한 글자씩 입력할 때마다 재렌더링되는 문제 해결
  const handleOpinionChange = useCallback(
    (e) => {
      setOpinion(e.target.value);
    },
    [setOpinion]
  );

  // 피드백 제출 함수 (사용자의 피드백이 담긴 like 값을 매개변수로 받아서 활용)
  const handleSubmit = async (likeValue) => {
    // 만족도를 선택하지 않은 경우
    if (likeValue === "NONE") {
      showToastMessage("만족 여부를 클릭해주세요");
      return;
    }
    
    // 의견을 입력하지 않은 경우
    if (!opinion.trim()) {
      showToastMessage("의견을 입력해주세요");
      return;
    }
    const SERVER_URI = config.serverUri;
    const uri = "/private/users/feedback";
    const url = `${SERVER_URI}${uri}`;

    try {
      const response = await apiFetch(url, {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          opinion: likeValue === "NONE" ? "" : opinion.trim(), //  "NONE"일 때 의견을 무조건 ""로 보냄
          like: likeValue, // 매개변수로 받은 like 값 서버에 전송
        }),
      });

      if (!response.ok) {
        throw new Error("Failed to submit feedback");
      }

      // "NONE"이 아닌 경우, 감사 모달 띄우기
      if (likeValue !== "NONE") {
        onSubmit();
      }

      onClose(); // 모달창 닫기
    } catch (error) {
      console.error("피드백 제출 실패:", error);
    }
  };

  return (
    <ModalBasic
      isOpen={isOpen}
      onClose={onClose}
      shouldCloseOnOverlayClick={false} // 외부 클릭으로 닫히지 않도록 설정
      width={window.innerWidth > 430 ? "400px" : "312px"}
      height="fit-content"
      maxHeight="calc(var(--vh, 1vh) * 100 - 32px)">
      <ModalWrapper>
        <HeaderWrapper>
          <Title>
            로우로우가 문제 해결에 도움이 되었나요?
          </Title>
          <CloseButton type="button" aria-label="만족도 창 닫기" onClick={onClose}>
            <img src={closeButton} alt="" width="24" height="24" />
          </CloseButton>
        </HeaderWrapper>

        <Description>
        만족도를 선택한 후 아래에 의견을 작성해주신 모든 분들께 
         감사의 의미로 <BoldText>5로우를 지급</BoldText>해드립니다.
        </Description>

        <RequiredText>
          <ExclamationIcon src={exclamationMark} alt="필수" />
          필수 선택 항목입니다.
        </RequiredText>

        <FeedbackOptions>
          <Option
            $selected={selected === "LIKE"}
            onClick={() => {
              setSelected(selected === "LIKE" ? "NONE" : "LIKE");
            }}>
            <Icon src={thumbsUpImg} alt="만족해요" />
            <OptionText>만족해요!</OptionText>
          </Option>

          <Option
            $selected={selected === "DISLIKE"}
            onClick={() => {
              setSelected(selected === "DISLIKE" ? "NONE" : "DISLIKE");
            }}
            $isDislike>
            <Icon src={thumbsDownImg} alt="아쉬워요" />
            <OptionText>아쉬워요!</OptionText>
          </Option>
        </FeedbackOptions>

        <RequiredText style={{ marginTop: '12px' }}>
          <ExclamationIcon src={exclamationMark} alt="필수" />
          {opinionGuide}
        </RequiredText>

        <OpinionInput
          placeholder="여러분의 소중한 의견이 향후 서비스 개선에 도움이 됩니다:)"
          value={opinion}
          onChange={handleOpinionChange}
        />

        <SubmitButtonWrapper>
          <SubmitButton
            onClick={() => handleSubmit(selected)} //  선택된 만족도 값 전달
            // disabled={selected === "NONE"} // 선택 안 하면 버튼 비활성화
          >
            제출하기
          </SubmitButton>
        </SubmitButtonWrapper>
      </ModalWrapper>
      
      {/* Toast 메시지 - 모달 외부에 절대 위치로 배치 */}
      {showToast && (
        <ToastMessage>
          {toastMessage}
        </ToastMessage>
      )}
    </ModalBasic>
  );
};

// 스타일 컴포넌트 정의
const ModalWrapper = styled.div`
  padding: 24px 24px 30px 24px;
  background: white;
  border-radius: 12px;

  @media (max-width: 430px) {
    padding: 20px 20px 24px 24px;
  }
`;

const HeaderWrapper = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 8px;
`;

const CloseButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 32px;
  height: 32px;
  padding: 4px;
  border: none;
  background: none;
  cursor: pointer;
`;

const Title = styled.div`
  flex: 1;
  min-width: 0;
  word-break: keep-all;
  color: #000;
  font-family: Pretendard;
  font-size: 20px;
  font-weight: 700;
  line-height: 30px;

  @media (max-width: 430px) {
    font-size: 16px;
    line-height: 26px;
  }
`;

const Description = styled.div`
  width: 308px;
  color: rgba(0, 0, 0, 0.6);
  font-family: Pretendard;
  font-size: 14px;
  font-weight: 500;
  line-height: 24px;
  letter-spacing: -0.28px;
  margin-top: 8px;

  @media (max-width: 430px) {
    width: auto;
  }
`;

const BoldText = styled.span`
  color: #000;
  font-weight: 700;
`;

const RequiredText = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  margin-top: 4px;
  color: rgba(0, 0, 0, 0.40);
  font-family: Pretendard;
  font-size: 12px;
  font-style: normal;
  font-weight: 500;
  line-height: 24px; /* 200% */
  letter-spacing: -0.24px;
`;

const ExclamationIcon = styled.img`
  width: 12px;
  height: 12px;
`;

const FeedbackOptions = styled.div`  
  display: flex;
  justify-content: space-between;
  gap: 8px;
  margin-top: 24px;

  @media (max-width: 430px) {
    margin-top: 16px;
  }
`;

const SubmitButtonWrapper = styled.div`
  display: flex;
  justify-content: center;
  margin-top: 16px;
`;

const Option = styled.div`
  width: 170px;
  height: 130px;
  border-radius: 12px;
  background: ${({ $selected, $isDislike }) =>
    $selected
      ? $isDislike
        ? "#EAEAEA"
        :  "linear-gradient(135deg, rgba(0, 243, 112, 0.20) 19.23%, rgba(0, 87, 255, 0.20) 78.8%), linear-gradient(135deg, rgba(0, 243, 112, 0.10) 19.23%, rgba(0, 87, 255, 0.10) 78.8%);"
      : "#F5F5F5"};
  border: ${({ $selected, $isDislike }) =>
    $selected
      ? $isDislike
        ? "1px solid rgba(0, 0, 0, 0.30)"
        : "1px solid rgba(26, 226, 118, 1)"
      : "0.5px solid rgba(0, 0, 0, 0.1)"};
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  cursor: pointer;

  @media (max-width: 430px) {
    width: 128px;
    height: 109px;
  }
`;

const Icon = styled.img`
  width: 64px;
  height: 64px;

  @media (max-width: 430px) {
    width: 48px;
    height: 48px;
  }
`;

const OptionText = styled.div`
  color: rgba(0, 0, 0, 0.8);
  text-align: center;
  font-family: Pretendard;
  font-size: 14px;
  font-weight: 600;
  line-height: 26px;
  margin-top: 9px;

  @media (max-width: 430px) {
    margin-top: 8.75px;
  }
`;

const OpinionInput = styled.textarea`
  width: 331px;
  height: 52px;
  margin-top: 4px;
  padding: 10px;
  border: 1px solid rgba(0, 0, 0, 0.1);
  border-radius: 8px;
  font-family: Pretendard;
  font-size: 12px;
  font-weight: 500;
  line-height: 20px;
  color: rgba(0, 0, 0, 0.3);
  letter-spacing: -0.6px;
  resize: none;
  outline: none; /* 선택시 검은 테두리 안 생기게 */
  color: #000; /* 입력한 텍스트는 검은색 */

  @media (max-width: 430px) {
    width: 243px;
    height: 52px;
    margin-top: 8px;
  }
  
  /* placeholder는 회색 유지 */
  &::placeholder {
    color: rgba(0, 0, 0, 0.3);

   
`;

const SubmitButton = styled.button`
  width: 133px;
  height: 40px;
  background: #000;
  border: none;
  border-radius: 6px;
  color: #00f370;
  text-align: center;
  font-family: Pretendard;
  font-size: 14px;
  font-weight: 500;
  line-height: 30px;
  letter-spacing: -0.28px;
  cursor: pointer;

  @media (max-width: 430px) {
    width: 108px;
  }
`;

const ToastMessage = styled.div`
  position: fixed;
  top: 50%;
  left: 50%;
  transform: translate(-50%, calc(-50% + 95px)); /* 모달 중앙에서 아래로 95px 이동 (제출하기 버튼 아래 위치) */
  background: rgba(0, 0, 0, 0.8);
  color: white;
  padding: 8px 16px;
  border-radius: 4px;
  font-family: Pretendard;
  font-size: 12px;
  font-weight: 500;
  text-align: center;
  z-index: 160; /* 모달보다 위에 표시 */
  animation: fadeInOut 3s ease-in-out;
  
  @keyframes fadeInOut {
    0% { opacity: 0; transform: translate(-50%, calc(-50% + 85px)); }
    10% { opacity: 1; transform: translate(-50%, calc(-50% + 95px)); }
    90% { opacity: 1; transform: translate(-50%, calc(-50% + 95px)); }
    100% { opacity: 0; transform: translate(-50%, calc(-50% + 105px)); }
  }
  
  @media (max-width: 430px) {
    transform: translate(-50%, calc(-50% + 80px)); /* 모바일에서는 위치 조정 */
    
    @keyframes fadeInOut {
      0% { opacity: 0; transform: translate(-50%, calc(-50% + 70px)); }
      10% { opacity: 1; transform: translate(-50%, calc(-50% + 80px)); }
      90% { opacity: 1; transform: translate(-50%, calc(-50% + 80px)); }
      100% { opacity: 0; transform: translate(-50%, calc(-50% + 90px)); }
    }
  }
`;

export default ModalUserFeedback;
